import BookingsRepository, {
  DeleteBookingInviteArgs,
  PostBookingInviteArgs,
} from '@/api/repositories/bookings'
import saveStateToStorage from '@/utils/saveStateToStorage'
import errorDispatcher from '@/utils/errorDispatcher'
import captureCheckinError from '@/utils/captureCheckinError'
import getSavedState from '@/utils/getSavedState'
import router from '@/router'
import { Booking, Checkin, Invite, Minor } from '@/types/api/types'
import { Bookings } from './store'

export const state: Bookings = {
  booking: getSavedState<Booking>('booking'),
  // The surname typed for the held booking. Never saved to storage, so it does not outlive the
  // page on a shared iPad. It is only a fallback, for a lookup that returned no token -- an API
  // without section-l/api#1400 -- on a booking whose owner name is null. ENG-3129.
  typedLastName: null,
}

// Codes can arrive as numbers -- a channel confirmation number serializes as one.
const normalizedCode = (code?: string | number | null) =>
  code == null ? '' : String(code).trim().toUpperCase()

const sameBookingCode = (
  a?: string | number | null,
  b?: string | number | null
) => Boolean(normalizedCode(a) && normalizedCode(a) === normalizedCode(b))

const heldBookingFor = (state: Bookings, bookingNo: string | number) =>
  sameBookingCode(state.booking?.code, bookingNo) ? state.booking : null

const typedLastNameFor = (state: Bookings, bookingNo: string | number) =>
  sameBookingCode(state.typedLastName?.bookingCode, bookingNo)
    ? state.typedLastName?.lastName
    : undefined

// Only the API's own missing-last-name rejection: typed `LastNameRequired` since section-l/api#1400,
// and recognised by its message from an API before that.
const isMissingLastNameError = error =>
  error?.type === 'LastNameRequired' ||
  (!error?.type && /last name is missing/i.test(error?.message || ''))

export const getters = {
  booking(state: Bookings) {
    return state.booking
  },
  minors(state: Bookings) {
    return state.booking?.minors
  },
  minorFromId(state: Bookings) {
    return (idToFind: number) =>
      state.booking?.minors.find(({ id }) => id === idToFind)
  },
  allCheckinsWillBeCompleted(state: Bookings, _, __, rootGetters) {
    const currentCheckin = rootGetters['checkins/currentCheckin']

    const completedAdultCheckins =
      state.booking?.checkins.filter(checkin => {
        if (checkin.status !== 'completed') return false

        return currentCheckin ? checkin.id != currentCheckin.id : true
      }) || []

    const completedMinors =
      state.booking?.minors.filter(minor => {
        const hasAllRequiredFields = (
          [
            'id',
            'dateOfBirth',
            'firstName',
            'lastName',
            'gender',
            'nationality',
            'photoKey', // while actually not required from minors, when skipped this field gets set to the string 'skipped'
          ] as (keyof Minor)[]
        )
          .concat(
            minor.nationality !== 'JP' && minor.country !== 'JP'
              ? (['idPhotoKey', 'passportNo'] as (keyof Minor)[])
              : []
          )
          .every(field => Boolean(minor[field]))

        return hasAllRequiredFields
      }) || []

    // we deduct 1 since this could be the checkin or the minor that could complete the entire booking
    const numberOfAdults = currentCheckin
      ? (state.booking?.numAdult || 1) - 1
      : Number(state.booking?.numAdult)
    const numberOfMinors = Number(state.booking?.numChild)

    return (
      completedAdultCheckins.length >= numberOfAdults &&
      completedMinors.length >= numberOfMinors
    )
  },
}

export const mutations = {
  SET_BOOKING(state: Bookings, booking: Booking) {
    // Only the public lookup returns a `lookupToken`; the invite and minor endpoints return the same
    // booking without one. Keep the held token across those, or the next refetch would have to
    // prove a surname again. ENG-3129.
    const heldToken = sameBookingCode(state.booking?.code, booking?.code)
      ? state.booking?.lookupToken
      : undefined
    state.booking =
      booking && !booking.lookupToken && heldToken
        ? { ...booking, lookupToken: heldToken }
        : booking
    saveStateToStorage<Bookings>('booking', state.booking)

    if (!sameBookingCode(state.typedLastName?.bookingCode, booking?.code)) {
      state.typedLastName = null
    }
  },

  ADD_CHECKIN(state: Bookings, checkin: Checkin) {
    if (!checkin.id) return

    if (state.booking) {
      const index = state.booking.checkins.findIndex(
        (ci: Checkin) => ci.id === checkin.id
      )
      index === -1
        ? state.booking?.checkins.push(checkin)
        : (state.booking.checkins[index] = checkin)
      saveStateToStorage<Bookings>('booking', state.booking)
    }
  },
  ADD_MINOR(state: Bookings, minor: Minor) {
    if (!minor.id) return

    if (state.booking) {
      const index = state.booking.minors.findIndex(
        (m: Minor) => m.id === minor.id
      )
      index === -1
        ? state.booking?.minors.push(minor)
        : (state.booking.minors[index] = minor)
      saveStateToStorage<Bookings>('booking', state.booking)
    }
  },

  // Kept in state only, never saved to storage. See `typedLastName` in the state above.
  SET_TYPED_LAST_NAME(state: Bookings, typedLastName: Bookings['typedLastName']) {
    state.typedLastName = typedLastName
  },

  RESET_BOOKING(state: Bookings) {
    state.booking = null
    saveStateToStorage<Bookings>('booking', state.booking)
    state.typedLastName = null
  },
}

export const actions = {
  setBooking({ commit }, bookingData: Partial<Booking>) {
    commit('SET_BOOKING', bookingData)
  },
  async fetchBooking(
    {
      commit,
      state,
      rootGetters,
    }: {
      commit: Function
      state: Bookings
      rootGetters: { [key: string]: any }
    },
    {
      bookingNo,
      lastName,
      lookupToken: linkToken,
      query,
    }: { query?: { [key: string]: any }, bookingNo: string; lastName?: string; lookupToken?: string }
  ) {
    if (!bookingNo) return

    // Starts the guest over from the code screen with nothing held, so ReservationConfirmation's
    // refetch on mount cannot raise this again.
    const askForLastNameAgain = async (reported: Record<string, any>) => {
      captureCheckinError(reported, { scene: 'fetchBooking' })
      await errorDispatcher('LastNameRequired', () => {
        commit('RESET_BOOKING')
        return router.push({ name: 'CheckinCode', query })
      })
    }

    // The API requires proof on every lookup of a Mews booking, refetches included. Refetches used
    // to re-send a surname: the owner's, which is null on a booking with no owner yet (a 422 and a
    // blank screen), or the one typed at lookup, which stops matching once the guest corrects their
    // own name mid-flow (a 404 and back to Home). They now send the `lookupToken` the last lookup
    // returned, which the API renews on every use. ENG-3129.
    //
    // The held token goes with every fetch of its booking, a typed name included, since a guest
    // starting over may type the surname they have since corrected. A surname rides along as a
    // fallback for a token that has expired: the typed one, else the owner's, else the one typed
    // earlier in this page.
    //
    // A link the API emailed carries a token of its own (`lt`), valid to the end of the stay. It
    // goes first: its `ln` stops matching once the owner corrects their surname, and a token held
    // on this device may have expired since.
    const enteredLastName = lastName && String(lastName).trim()
    const held = heldBookingFor(state, bookingNo)
    const lookupToken = linkToken || held?.lookupToken || undefined
    const lastNameToSend =
      enteredLastName ||
      held?.lastName ||
      typedLastNameFor(state, bookingNo) ||
      undefined

    // A Mews booking we already hold, with nothing to prove it by, cannot be fetched: the request
    // is a guaranteed 422. Say so instead of sending it, and report it -- reaching this means some
    // path lost the token. ENG-3129.
    //
    // A booking not yet held is still looked up: an invite link carries a code but no surname, and
    // the API is the one to say whether that is enough. Its 422 is handled below, visibly.
    if (!lastNameToSend && !lookupToken && held?.sourcePms === 'mews') {
      return askForLastNameAgain({ type: 'LastNameRequired' })
    }

    try {
      const response = await BookingsRepository.getBooking({
        bookingNo,
        lastName: lastNameToSend,
        lookupToken,
        propCode: rootGetters['properties/code'],
      })
      const booking = response.data
      commit('SET_BOOKING', booking)
      if (enteredLastName) {
        commit('SET_TYPED_LAST_NAME', {
          bookingCode: String(booking?.code || bookingNo),
          lastName: enteredLastName,
        })
      }
      return booking
    } catch (error) {
      // The API's missing-last-name 422, which errorDispatcher has no copy of its own for. The
      // server's text still reaches Sentry as `serverMessage`.
      if (isMissingLastNameError(error)) {
        return askForLastNameAgain({ ...error, type: 'LastNameRequired' })
      }
      await errorDispatcher(
        error?.type,
        () => router.push({ name: 'Home', query }),
        error?.metadata
      )
    }
  },
  addCheckin(
    { commit }: { commit: Function },
    { checkin }: { checkin: Checkin }
  ) {
    return commit('ADD_CHECKIN', checkin)
  },
  async fetchInvite(
    _,
    { bookingCode, inviteUuid }
  ): Promise<Invite | undefined> {
    try {
      const response = await BookingsRepository.getInvite({
        bookingCode,
        inviteUuid,
      })
      return response.data
    } catch (error) {
      if (error instanceof Error) {
        await errorDispatcher(error.type, () => router.push({ name: 'Home' }))
      }
    }
  },
  async createInvite(
    { commit },
    { bookingCode, invite }: PostBookingInviteArgs
  ): Promise<Booking> {
    const response = await BookingsRepository.createInvite({
      bookingCode,
      invite,
    })
    const booking = response.data
    commit('SET_BOOKING', booking)
    return booking
  },
  async deleteInvite(
    { commit },
    { bookingCode, inviteUuid }: DeleteBookingInviteArgs
  ): Promise<Booking> {
    const response = await BookingsRepository.deleteInvite({
      bookingCode,
      inviteUuid,
    })
    const booking = response.data
    commit('SET_BOOKING', booking)
    return booking
  },
  async sendInviteEmails({ }, { bookingCode }: { bookingCode: string }) {
    const response = await BookingsRepository.sendInviteEmails({ bookingCode })
    return response.data
  },
  resetBooking({ commit }: { commit: Function }) {
    return commit('RESET_BOOKING')
  },
  async checkout(
    { rootGetters }: { rootGetters: { [key: string]: string } },
    { roomNumber, roomCode, lastName }: { roomNumber: number; roomCode?: string; lastName?: string }
  ) {
    const response = await BookingsRepository.checkout({
      roomNumber,
      roomCode,
      lastName,
      propCode: rootGetters['properties/code'],
    })
    return response.data
  },
  async addMinorToBooking({ commit }, updatedMinor: Partial<Minor>) {
    if (!state.booking?.code)
      throw Error('Cannot update Minor without a Booking Code')
    commit('ADD_MINOR', updatedMinor)
    return updatedMinor
  },
}
